import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import './NotificationSettings.css';

function NotificationSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/notification-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to fetch notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChannelToggle = async (channel, enabled) => {
    try {
      const updatedSettings = {
        ...settings,
        channels: {
          ...settings.channels,
          [channel]: {
            ...settings.channels[channel],
            enabled
          }
        }
      };
      setSettings(updatedSettings);

      await api.put('/notification-settings/channels', {
        [channel]: { enabled }
      });

      toast.success(`${channel} notifications ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating channel:', error);
      toast.error('Failed to update channel settings');
      fetchSettings(); // Revert on error
    }
  };

  const handlePreferenceChange = async (type, channel, value) => {
    try {
      const updatedSettings = {
        ...settings,
        preferences: {
          ...settings.preferences,
          [type]: {
            ...settings.preferences[type],
            [channel]: value
          }
        }
      };
      setSettings(updatedSettings);

      await api.put(`/notification-settings/preferences/${type}`, {
        [channel]: value
      });
    } catch (error) {
      console.error('Error updating preference:', error);
      toast.error('Failed to update preference');
      fetchSettings(); // Revert on error
    }
  };

  const handleTestChannel = async (channel) => {
    try {
      setTesting({ ...testing, [channel]: true });
      const response = await api.post('/notification-settings/test', {
        channel
      });

      if (response.data.result?.demo) {
        toast.info(`Demo mode: Test ${channel} notification logged`);
      } else {
        toast.success(`Test ${channel} notification sent!`);
      }
    } catch (error) {
      console.error('Error testing channel:', error);
      toast.error(`Failed to send test ${channel} notification`);
    } finally {
      setTesting({ ...testing, [channel]: false });
    }
  };

  const handleQuietHoursUpdate = async (quietHours) => {
    try {
      setSaving(true);
      await api.put('/notification-settings/quiet-hours', quietHours);
      toast.success('Quiet hours updated successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error updating quiet hours:', error);
      toast.error('Failed to update quiet hours');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading notification settings...</div>
      </Layout>
    );
  }

  if (!settings) {
    return (
      <Layout>
        <div className="error">Failed to load notification settings</div>
      </Layout>
    );
  }

  const notificationTypes = [
    {
      category: 'Orders',
      types: [
        { key: 'orderPlaced', label: 'Order Placed' },
        { key: 'orderConfirmed', label: 'Order Confirmed' },
        { key: 'orderShipped', label: 'Order Shipped' },
        { key: 'orderDelivered', label: 'Order Delivered' },
        { key: 'orderCancelled', label: 'Order Cancelled' }
      ]
    },
    {
      category: 'Invoices',
      types: [
        { key: 'invoiceGenerated', label: 'Invoice Generated' },
        { key: 'invoicePaid', label: 'Invoice Paid' },
        { key: 'invoiceOverdue', label: 'Invoice Overdue' }
      ]
    },
    {
      category: 'Payments',
      types: [
        { key: 'paymentReceived', label: 'Payment Received' },
        { key: 'paymentFailed', label: 'Payment Failed' },
        { key: 'refundProcessed', label: 'Refund Processed' }
      ]
    },
    {
      category: 'Support',
      types: [
        { key: 'ticketCreated', label: 'Ticket Created' },
        { key: 'ticketUpdated', label: 'Ticket Updated' },
        { key: 'ticketResolved', label: 'Ticket Resolved' }
      ]
    },
    {
      category: 'Account',
      types: [
        { key: 'accountVerified', label: 'Account Verified' },
        { key: 'passwordChanged', label: 'Password Changed' },
        { key: 'loginAlert', label: 'Login Alert' }
      ]
    },
    {
      category: 'Marketing',
      types: [
        { key: 'promotions', label: 'Promotions' },
        { key: 'newsletter', label: 'Newsletter' }
      ]
    }
  ];

  return (
    <Layout>
      <div className="notification-settings">
        <h1>Notification Settings</h1>
        <p className="subtitle">Manage how you receive notifications</p>

        {/* Channels Section */}
        <div className="settings-section">
          <h2>Notification Channels</h2>
          <p className="section-desc">Enable or disable notification channels</p>

          <div className="channels-grid">
            <div className="channel-card">
              <div className="channel-header">
                <div className="channel-icon">📱</div>
                <div className="channel-info">
                  <h3>In-App Notifications</h3>
                  <p>Receive notifications within the app</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.channels.inApp.enabled}
                    onChange={(e) => handleChannelToggle('inApp', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <button
                className="btn-test"
                onClick={() => handleTestChannel('inApp')}
                disabled={!settings.channels.inApp.enabled || testing.inApp}
              >
                {testing.inApp ? 'Testing...' : 'Test'}
              </button>
            </div>

            <div className="channel-card">
              <div className="channel-header">
                <div className="channel-icon">📧</div>
                <div className="channel-info">
                  <h3>Email Notifications</h3>
                  <p>Receive notifications via email</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.channels.email.enabled}
                    onChange={(e) => handleChannelToggle('email', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <button
                className="btn-test"
                onClick={() => handleTestChannel('email')}
                disabled={!settings.channels.email.enabled || testing.email}
              >
                {testing.email ? 'Testing...' : 'Test'}
              </button>
            </div>

            <div className="channel-card">
              <div className="channel-icon">💬</div>
              <div className="channel-header">
                <div className="channel-info">
                  <h3>SMS Notifications</h3>
                  <p>Receive notifications via SMS</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.channels.sms.enabled}
                    onChange={(e) => handleChannelToggle('sms', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <button
                className="btn-test"
                onClick={() => handleTestChannel('sms')}
                disabled={!settings.channels.sms.enabled || testing.sms}
              >
                {testing.sms ? 'Testing...' : 'Test'}
              </button>
            </div>

            <div className="channel-card">
              <div className="channel-header">
                <div className="channel-icon">📲</div>
                <div className="channel-info">
                  <h3>WhatsApp Notifications</h3>
                  <p>Receive notifications via WhatsApp</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.channels.whatsapp.enabled}
                    onChange={(e) => handleChannelToggle('whatsapp', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <button
                className="btn-test"
                onClick={() => handleTestChannel('whatsapp')}
                disabled={!settings.channels.whatsapp.enabled || testing.whatsapp}
              >
                {testing.whatsapp ? 'Testing...' : 'Test'}
              </button>
            </div>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="settings-section">
          <h2>Notification Preferences</h2>
          <p className="section-desc">Choose which notifications you want to receive on each channel</p>

          {notificationTypes.map((category) => (
            <div key={category.category} className="preference-category">
              <h3>{category.category}</h3>
              <div className="preferences-table">
                <div className="preferences-header">
                  <div className="pref-label">Notification Type</div>
                  <div className="pref-channel">In-App</div>
                  <div className="pref-channel">Email</div>
                  <div className="pref-channel">SMS</div>
                  <div className="pref-channel">WhatsApp</div>
                </div>

                {category.types.map((type) => (
                  <div key={type.key} className="preferences-row">
                    <div className="pref-label">{type.label}</div>
                    <div className="pref-channel">
                      <input
                        type="checkbox"
                        checked={settings.preferences[type.key]?.inApp || false}
                        onChange={(e) =>
                          handlePreferenceChange(type.key, 'inApp', e.target.checked)
                        }
                        disabled={!settings.channels.inApp.enabled}
                      />
                    </div>
                    <div className="pref-channel">
                      <input
                        type="checkbox"
                        checked={settings.preferences[type.key]?.email || false}
                        onChange={(e) =>
                          handlePreferenceChange(type.key, 'email', e.target.checked)
                        }
                        disabled={!settings.channels.email.enabled}
                      />
                    </div>
                    <div className="pref-channel">
                      <input
                        type="checkbox"
                        checked={settings.preferences[type.key]?.sms || false}
                        onChange={(e) =>
                          handlePreferenceChange(type.key, 'sms', e.target.checked)
                        }
                        disabled={!settings.channels.sms.enabled}
                      />
                    </div>
                    <div className="pref-channel">
                      <input
                        type="checkbox"
                        checked={settings.preferences[type.key]?.whatsapp || false}
                        onChange={(e) =>
                          handlePreferenceChange(type.key, 'whatsapp', e.target.checked)
                        }
                        disabled={!settings.channels.whatsapp.enabled}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Quiet Hours */}
        <div className="settings-section">
          <h2>Quiet Hours</h2>
          <p className="section-desc">Set times when you don't want to receive notifications</p>

          <div className="quiet-hours-card">
            <label className="toggle-row">
              <span>Enable Quiet Hours</span>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.quietHours.enabled}
                  onChange={(e) =>
                    handleQuietHoursUpdate({
                      ...settings.quietHours,
                      enabled: e.target.checked
                    })
                  }
                />
                <span className="toggle-slider"></span>
              </label>
            </label>

            {settings.quietHours.enabled && (
              <div className="time-inputs">
                <div className="input-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={settings.quietHours.startTime || '22:00'}
                    onChange={(e) =>
                      handleQuietHoursUpdate({
                        ...settings.quietHours,
                        startTime: e.target.value
                      })
                    }
                  />
                </div>
                <div className="input-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={settings.quietHours.endTime || '08:00'}
                    onChange={(e) =>
                      handleQuietHoursUpdate({
                        ...settings.quietHours,
                        endTime: e.target.value
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default NotificationSettings;
